import type {
  DealSource,
  ExportDealsParams,
  FilterDataType,
  GetDealDocumentHTTPHandlerResponse,
  GetDetailedDealDocumentHTTPHandlerResponse,
  GetLandlordBuildingsHTTPHandlerResponse,
  GetLandlordDealsExportHTTPHandlerResponse,
  LeaseDetailsFormBase,
  LeaseDetailsRenter,
  LeaseDateFilters,
} from '../types';

import { apiUri } from '@/constants/Constants';
import getEnv from '@/env';
import { createAxiosClient } from '@/utils/axios/createAxiosClient';
import { DateFormat } from '@/shared/libs/time.lib';
import { ApplicationTabs } from '../types';

const createSearchQuery = (
  applicationTab: string,
  limit?: number,
  lastTimestamp?: number | null,
  search?: string,
  selectedStatus?: FilterDataType[],
  selectedBuildings?: FilterDataType[],
  leaseFilter?: LeaseDateFilters,
) => ({
  filters: {
    ...(search && {
      search_text: search
        // remove white spaces on begin and end of search text
        .trim()
        // replace any number of repeated spaces with only one
        .replace(/\s\s+/g, ' '),
    }),
    ...(selectedStatus?.length &&
      applicationTab === ApplicationTabs.InProgress && {
        deal_status: {
          in: selectedStatus.flatMap((item) => item.name),
        },
      }),
    ...(selectedBuildings?.length && {
      building_id: { in: selectedBuildings.map((item) => item.id) },
    }),
    ...(applicationTab === ApplicationTabs.BondIssued && {
      bonds_issued: true,
    }),
    ...(applicationTab === ApplicationTabs.BondIssued &&
      leaseFilter && {
        lease: {
          lease_start_date: {
            from: leaseFilter.startDateRange.from?.format(DateFormat.Calendar),
            to: leaseFilter.startDateRange.to?.format(DateFormat.Calendar),
          },
          lease_end_date: {
            from: leaseFilter.endDateRange.from?.format(DateFormat.Calendar),
            to: leaseFilter.endDateRange.to?.format(DateFormat.Calendar),
          },
        },
      }),
    ...(applicationTab === ApplicationTabs.InProgress &&
      leaseFilter && {
        lease: {
          lease_start_date: {
            from: leaseFilter.startDateRange.from?.format(DateFormat.Calendar),
            to: leaseFilter.startDateRange.to?.format(DateFormat.Calendar),
          },
        },
      }),
  },
  pagination: {
    ...(limit && { limit }),
    ...(lastTimestamp && { last_seen_timestamp: lastTimestamp }),
  },
});

export const DdClient = createAxiosClient({
  options: {
    baseURL: getEnv().deals_dashboard_service_url,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 300000,
  },
});

export const getLandlordDeals = async (
  applicationTab: string,
  limit: number,
  lastTimestamp?: number | null,
  search?: string,
  selectedStatus?: FilterDataType[],
  selectedBuildings?: FilterDataType[],
  isFromPagination?: boolean,
  leaseFilter?: LeaseDateFilters,
) => {
  const payloadObject = createSearchQuery(
    applicationTab,
    limit,
    lastTimestamp,
    search,
    selectedStatus,
    selectedBuildings,
    leaseFilter,
  );

  const response = await DdClient.post(
    isFromPagination ? apiUri.getAllDealsRecordsCount : apiUri.getAllDeals,
    payloadObject,
  );

  return response.data.data;
};

export const exportDeals = async ({
  applicationTab,
  allRecords,
  limit,
  lastTimestamp,
  search,
  selectedStatus,
  selectedBuildings,
  leaseDates,
}: ExportDealsParams): Promise<GetLandlordDealsExportHTTPHandlerResponse> => {
  let payloadObject;

  if (allRecords) {
    payloadObject = {
      all_records: allRecords,
      queries: {
        filters: {
          ...(applicationTab === ApplicationTabs.BondIssued && {
            bonds_issued: true,
          }),
        },
      },
    };
  } else {
    payloadObject = {
      all_records: allRecords,
      queries: createSearchQuery(
        applicationTab,
        limit,
        lastTimestamp,
        search,
        selectedStatus,
        selectedBuildings,
        leaseDates,
      ),
    };
  }

  const response = await DdClient.post<GetLandlordDealsExportHTTPHandlerResponse>(
    apiUri.getAllDealsExport,
    payloadObject,
  );
  return response.data;
};

export const getLandlordBuildings = async (filters: {
  searchText?: string;
  page?: number;
  limit?: number;
}) => {
  const queryParams: Record<string, string | number> = {};
  if (filters.searchText) queryParams.search_text = filters.searchText;
  if (filters.page) queryParams.page = filters.page;
  if (filters.limit) queryParams.limit = filters.limit;
  const response = await DdClient.get<GetLandlordBuildingsHTTPHandlerResponse>(
    apiUri.getLandlordBuildings,
    {
      params: queryParams,
    },
  );

  return response.data.data;
};

export const getDealBondDocument = async (dealId: string, source: string) => {
  const queryParams: Record<string, string | number> = {
    document_type: 'signed-bond',
    source,
  };
  const response = await DdClient.get<GetDealDocumentHTTPHandlerResponse>(
    apiUri.getDealBondDocument.replace('{dealId}', dealId),
    {
      params: queryParams,
    },
  );

  return response.data.data;
};

export const getDetailedDocument = async (dealId: string, source: string) => {
  const response = await DdClient.get<GetDetailedDealDocumentHTTPHandlerResponse>(
    apiUri.getDetailedDealDocument.replace('{dealId}', dealId),
    {
      params: {
        source,
      },
    },
  );

  return response.data.data;
};

export type SubmitLeaseDetailsParams = Omit<LeaseDetailsFormBase, 'renters'> & {
  renters: Omit<LeaseDetailsRenter, 'id'>[];
  deal_id: number;
  deal_source: string;
  has_custom_rent_coverage: boolean;
  has_custom_deposit_coverage: boolean;
};

export type SubmitLeaseDetailsResponse = {
  status_code: number;
  data: {
    submission: {
      manual_underwriting: boolean;
      evaluation_result: string;
      deal_source: DealSource;
      address: string;
      deal_id: number;
      deposit_coverage_override: number | null;
      free_rent: number;
      gross_monthly_rent: number;
      has_custom_deposit_coverage: boolean;
      has_custom_rent_coverage: boolean;
      lease_end_date: string;
      lease_start_date: string;
      prepaid_rent: number;
      rent_coverage_override: number | null;
      screening_status: string;
      submission_id: string;
      submission_status: string;
      submitted_at: string;
      unit: string;
    };
  };
};

export const submitLeaseDetails = async (leaseData: SubmitLeaseDetailsParams) => {
  const { status, data } = await DdClient.post<SubmitLeaseDetailsResponse>(
    apiUri.submitLeaseDetails,
    leaseData,
  );

  if (status >= 300 || data.status_code >= 300 || !data?.data) {
    throw new Error('Failed to submit lease details');
  }

  return data.data;
};
